import { useState, useEffect, useCallback } from 'react';
import './App.css';
import AddItemForm   from './components/AddItemForm';
import WishList      from './components/WishList';
import Dashboard     from './components/Dashboard';
import History       from './components/History';
import AuthScreen    from './components/AuthScreen';
import LandingPage   from './components/LandingPage';
import ProfilePage   from './components/ProfilePage';
import { signOut, onAuthStateChange } from './services/auth';
import { fetchItems, createItem, updateItem, deleteItem } from './services/items';
import { getProfile } from './services/profile';

const logo = '/simmer-logo-cropped.png';

function genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export default function App() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authEntry,   setAuthEntry]   = useState(null); // null=landing | 'signup' | 'signin'

  useEffect(() => {
    const { data: { subscription } } = onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      // INITIAL_SESSION fires after Supabase has finished processing any
      // ?code= token in the URL (email verification callback). Only then
      // is it safe to stop showing the loading screen.
      if (event === 'INITIAL_SESSION') {
        setAuthLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut();
    setItems([]);
    setSimmerDays(5);
    setActiveTab('list');
  }, []);

  // ── Profile / preferences ─────────────────────────────────────────────────
  const [simmerDays, setSimmerDays] = useState(3);

  useEffect(() => {
    if (!user) return;
    getProfile(user.id).then(({ data }) => {
      if (data?.simmer_days) setSimmerDays(data.simmer_days);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Items ─────────────────────────────────────────────────────────────────
  const [items,     setItems]     = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [showForm,  setShowForm]  = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchItems(user.id).then(({ data }) => {
      if (data) setItems(data);
    });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Promote waiting → ready client-side as expiresAt passes (no DB call needed)
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      const now = Date.now();
      setItems(prev => {
        const hasExpired = prev.some(i => i.status === 'waiting' && i.expiresAt <= now);
        if (!hasExpired) return prev;
        return prev.map(i =>
          i.status === 'waiting' && i.expiresAt <= now ? { ...i, status: 'ready' } : i
        );
      });
    }, 60_000);
    return () => clearInterval(id);
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Item mutations ────────────────────────────────────────────────────────
  const addItem = useCallback(async (itemData) => {
    const now = Date.now();
    const days = itemData.simmerDays ?? simmerDays;
    const item = {
      id:        genId(),
      userId:    user?.id,
      url:       itemData.url,
      name:      itemData.name || extractDomain(itemData.url),
      price:     itemData.price ? parseFloat(itemData.price) : null,
      image:     itemData.image    || null,
      category:  itemData.category || null,
      siteName:  itemData.siteName || null,
      addedAt:   now,
      expiresAt: now + days * 24 * 60 * 60 * 1000,
      status:    'waiting',
      decidedAt: null,
    };
    const { error } = await createItem(item);
    if (!error) setItems(prev => [item, ...prev]);
    setShowForm(false);
  }, [user?.id, simmerDays]);

  const decideItem = useCallback(async (id, decision) => {
    const updates = { status: decision, decidedAt: Date.now() };
    await updateItem(id, updates);
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...updates } : i));
  }, []);

  const deleteItemById = useCallback(async (id) => {
    await deleteItem(id);
    setItems(prev => prev.filter(i => i.id !== id));
  }, []);

  // Expire the simmer timer immediately so the item becomes "ready"
  const skipTimer = useCallback(async (id) => {
    const now = Date.now();
    await updateItem(id, { expiresAt: now });
    setItems(prev => prev.map(i =>
      i.id === id ? { ...i, expiresAt: now, status: 'ready' } : i
    ));
  }, []);

  // ── Derived data ──────────────────────────────────────────────────────────
  const waitingItems = items.filter(i => i.status === 'waiting');
  const readyItems   = items.filter(i => i.status === 'ready');
  const historyItems = items.filter(i => i.status === 'bought' || i.status === 'skipped');
  const readyCount   = readyItems.length;

  const skippedItems      = items.filter(i => i.status === 'skipped');
  const totalSavedAllTime = skippedItems.reduce((s, i) => s + (i.price || 0), 0);
  const skippedCount      = skippedItems.length;

  const userInitial = user?.email?.[0]?.toUpperCase() || '?';

  // ── Render gates ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="auth-loading-screen">
        <img src={logo} alt="Simmer" className="auth-loading-logo" />
      </div>
    );
  }

  if (!user && authEntry === null) {
    return (
      <LandingPage
        onGetStarted={() => setAuthEntry('signup')}
        onSignIn={() => setAuthEntry('signin')}
      />
    );
  }

  if (!user) {
    return <AuthScreen onAuth={setUser} initialView={authEntry} />;
  }

  // ── Profile page props ────────────────────────────────────────────────────
  const profileProps = {
    user,
    items,
    simmerDays,
    onSimmerDaysChange: setSimmerDays,
    onSignOut:          handleSignOut,
    onBack:             () => setActiveTab('list'),
  };

  // ── Main app ──────────────────────────────────────────────────────────────
  return (
    <div className="app-shell">

      {/* Desktop-only sidebar */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <h1 className="logo">Simmer</h1>
          <p className="tagline">impulse shopping is COOKED</p>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`sidebar-tab ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            Simmering
            {readyCount > 0 && <span className="badge">{readyCount}</span>}
          </button>
          <button
            className={`sidebar-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Savings
          </button>
          <button
            className={`sidebar-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            The Archive
          </button>
        </nav>

        {totalSavedAllTime > 0 && (
          <div className="sidebar-savings">
            <p className="sidebar-savings-label">All-time saved</p>
            <p className="sidebar-savings-amount">${totalSavedAllTime.toFixed(2)}</p>
            <p className="sidebar-savings-sub">
              {skippedCount} item{skippedCount !== 1 ? 's' : ''} skipped
            </p>
          </div>
        )}

        <div className="sidebar-footer">
          <button
            className="sidebar-profile-btn"
            onClick={() => setActiveTab('profile')}
            aria-label="Account"
          >
            <span className="profile-avatar">{userInitial}</span>
            <span className="sidebar-user-email">{user.email}</span>
          </button>
          <button className="sidebar-signout" onClick={handleSignOut}>Sign out</button>
        </div>
      </aside>

      {/* Main app column */}
      <div className="app">

        {/* Mobile-only header */}
        <header className="app-header">
          <div className="header-inner">
            <h1 className="logo">Simmer</h1>
            <p className="tagline-small">impulse shopping is COOKED</p>
          </div>
          <div className="header-actions">
            <button
              className="header-icon-btn"
              onClick={() => setActiveTab('history')}
              aria-label="The Archive"
            >
              <span className="material-symbols-outlined">inventory_2</span>
            </button>
            <button
              className="header-profile-btn"
              onClick={() => setActiveTab(t => t === 'profile' ? 'list' : 'profile')}
              aria-label="Account"
            >
              <span className="profile-avatar">{userInitial}</span>
            </button>
          </div>
        </header>

        {/* Bottom tab bar */}
        <nav className="tab-bar">
          <button
            className={`tab ${activeTab === 'list' ? 'active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            <span className="material-symbols-outlined">timer</span>
            Simmering
            {readyCount > 0 && <span className="badge">{readyCount}</span>}
          </button>
          <button
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <span className="material-symbols-outlined">savings</span>
            Savings
          </button>
          <button
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <span className="material-symbols-outlined">inventory_2</span>
            Archive
          </button>
          <button
            className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab(t => t === 'profile' ? 'list' : 'profile')}
          >
            <span className="material-symbols-outlined">person</span>
            Profile
          </button>
        </nav>

        <main className="main-content">
          {activeTab === 'list' && (
            <WishList
              waitingItems={waitingItems}
              readyItems={readyItems}
              onDecide={decideItem}
              onDelete={deleteItemById}
              onSkipTimer={skipTimer}
              onAddItem={() => setShowForm(true)}
            />
          )}
          {activeTab === 'dashboard' && <Dashboard items={items} />}
          {activeTab === 'history'   && <History items={historyItems} />}
          {activeTab === 'profile'   && <ProfilePage {...profileProps} />}
        </main>

        {activeTab === 'list' && !showForm && (
          <button className="fab" onClick={() => setShowForm(true)} aria-label="Add item">
            + Add something to simmer
          </button>
        )}

        {showForm && (
          <AddItemForm onAdd={addItem} onCancel={() => setShowForm(false)} />
        )}
      </div>
    </div>
  );
}

function extractDomain(url) {
  try { return new URL(url).hostname.replace('www.', ''); }
  catch { return url || 'Item'; }
}
